import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDate } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Supplier } from "../types";
import { MessageSquare, FileText } from "lucide-react";

interface SupplierHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplier: Supplier;
}

interface Communication {
  id: string;
  type: string;
  content: string;
  document_url?: string;
  created_at: string;
}

export const SupplierHistoryDialog = ({
  open,
  onOpenChange,
  supplier,
}: SupplierHistoryDialogProps) => {
  const { data: communications, isLoading } = useQuery({
    queryKey: ["supplier-communications", supplier.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("supplier_communications")
        .select("*")
        .eq("supplier_id", supplier.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Communication[];
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Historique des communications - {supplier.name}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[400px] pr-4">
          {isLoading ? (
            <div className="text-center py-4">Chargement...</div>
          ) : communications?.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              Aucune communication enregistrée
            </div>
          ) : (
            <div className="space-y-4">
              {communications?.map((comm) => (
                <div
                  key={comm.id}
                  className="p-4 border rounded-lg space-y-2 hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {comm.type === "message" ? (
                      <MessageSquare className="h-4 w-4" />
                    ) : (
                      <FileText className="h-4 w-4" />
                    )}
                    <span>{formatDate(comm.created_at)}</span>
                  </div>
                  <p className="text-sm">{comm.content}</p>
                  {comm.document_url && (
                    <a
                      href={comm.document_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                    >
                      <FileText className="h-4 w-4" />
                      Voir le document
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};